import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import { Grid } from '@instinet-analytics/semantic-ui-components';
import orderBy from 'lodash/orderBy';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';

import './Messages.scss';
import MessagePanel from 'widgets/supportVisualisation/Content/Messages/MessagePanel';
import { toDateString } from 'widgets/supportVisualisation/SupportVisualisationHelpers';
import {
    extractLogMessagesFromLogs,
    LogMessagesLogItem,
} from 'logging/store/LogMessagesStoreService';
import {
    LogLevel,
    toLogLevelString,
    MessageData,
} from '@instinet-analytics/core-lib';
import QuickSearch from 'utils/components/QuickSearch/QuickSearch';
import { PostingMessage } from 'communication/streaming/SockJSStreamingService';

export interface LogMessageRow extends LogMessagesLogItem {
    rowId: number;
    levelDesc: string;
}

export default function Messages(props: VisualisationContextProps) {
    const { state, height, width } = props;

    const [selectedMessage, setSelectedMessage] = useState<
        LogMessageRow | undefined
    >(undefined);

    const [quickSearch, setQuickSearch] = useState('');

    let messages = extractLogMessagesFromLogs(state?.logMessages).filter(
        (m) => !m.message.startsWith(PostingMessage)
    );
    if (messages.length === 0) {
        messages = extractLogMessagesFromLegacyState(
            state as unknown as LegacyContactSupportState
        );
    }
    if (!messages || messages.length === 0) {
        return <div>None found in state</div>;
    }

    const rowData = extractMessages(messages, quickSearch);

    const columnDefs = getColumns();

    const onSelectedRowIdsChanged = (rowDataIds: string[]) => {
        if (rowDataIds.length !== 1) {
            setSelectedMessage(undefined);
        }
        const row = rowData.find((r) => r.rowId.toString() === rowDataIds[0]);
        setSelectedMessage(row);
    };

    const panelHeight = height / 2;

    const updateQuickSearch = (s: string) => {
        setQuickSearch(s);
    };

    return (
        <div className={'Messages'}>
            <div className="messages-grid">
                <div className="filters">
                    <div className="title">Quick Search:</div>
                    <QuickSearch
                        filterApplied={false}
                        filteredRowCount={0}
                        searchText={quickSearch}
                        onSearchChange={updateQuickSearch}
                        placeholder={'Enter text'}
                    />
                </div>
                <Grid
                    autoSizeColumns={true}
                    singleSelectOnly={true}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    getRowDataId={(r) => r.rowId.toString()}
                    onSelectedRowIdsChanged={onSelectedRowIdsChanged}
                />
            </div>
            {selectedMessage && (
                <MessagePanel
                    msg={selectedMessage}
                    height={panelHeight}
                    width={width}
                />
            )}
        </div>
    );
}

function extractMessages(
    messages: LogMessagesLogItem[],
    quickSearch: string
): LogMessageRow[] {
    const res: LogMessageRow[] = [];
    let rowId = 0;

    for (const msg of messages) {
        res.push({
            rowId: rowId++,
            levelDesc: toLogLevelString(msg.level),
            ...msg,
        });
    }

    let ret = res;

    if (quickSearch) {
        const applyQuickSearch = (r: LogMessageRow) =>
            r.source.toLowerCase().indexOf(quickSearch) > -1 ||
            r.message.toLowerCase().indexOf(quickSearch) > -1 ||
            r.levelDesc.toLowerCase().indexOf(quickSearch) > -1;
        ret = res.filter(applyQuickSearch);
    }

    return orderBy(ret, ['timestamp'], ['desc']);
}

function getColumns(): ColDef[] {
    return [
        {
            field: 'timestamp',
            headerName: 'Time',
            resizable: true,
            width: 80,
            cellRenderer: (props: { value: Date }) => {
                return <span>{toDateString(props.value)}</span>;
            },
        },
        {
            field: 'levelDesc',
            headerName: 'Level',
            width: 70,
        },
        {
            field: 'source',
            headerName: 'Source',
            sortable: true,
            resizable: true,
        },
        {
            field: 'message',
            headerName: 'Message',
            resizable: true,
        },
    ];
}

interface LegacyContactSupportState {
    state: {
        logMessages: {
            messages: Array<{
                time: Date;
                level: LogLevel;
                source: string;
                message: string;
                data?: MessageData;
            }>;
        };
    };
}

function extractLogMessagesFromLegacyState(
    state?: LegacyContactSupportState
): LogMessagesLogItem[] {
    const legacyMessages = state?.state?.logMessages?.messages;
    if (!legacyMessages) {
        return [];
    }
    return legacyMessages.map((i) => ({
        timestamp: i.time,
        ...i,
    }));
}
